'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Lock, ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import {
  QUALITY_TIERS,
  ExportQuality,
  PlanType,
  getQualityUpgradeMessage
} from '@/lib/utils/export-quality';

interface ExportQualityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedQuality: ExportQuality;
  onSelectQuality: (quality: ExportQuality) => void;
  currentPlan: PlanType;
}

export function ExportQualityModal({
  open,
  onOpenChange,
  selectedQuality,
  onSelectQuality,
  currentPlan,
}: ExportQualityModalProps) {
  const router = useRouter();

  const handleSelect = (quality: ExportQuality) => {
    const tier = QUALITY_TIERS[quality];
    const isLocked = !tier.requiredPlans.includes(currentPlan);

    if (isLocked) {
      return;
    }

    onSelectQuality(quality);
    onOpenChange(false);
  };

  const handleUpgrade = () => {
    onOpenChange(false);
    router.push('/dashboard/settings/billing');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Choose Export Quality</DialogTitle>
          <DialogDescription>
            Select the video quality for your export. Higher quality means better resolution but larger file sizes and longer processing times.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-4">
          {(Object.entries(QUALITY_TIERS) as [ExportQuality, typeof QUALITY_TIERS[ExportQuality]][]).map(([key, tier]) => {
            const isSelected = key === selectedQuality;
            const isLocked = !tier.requiredPlans.includes(currentPlan);
            const canUpgrade = isLocked && currentPlan !== 'enterprise';

            return (
              <button
                key={key}
                onClick={() => handleSelect(key)}
                disabled={isLocked}
                className={`
                  relative flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left
                  ${isSelected && !isLocked
                    ? 'border-primary bg-primary/5'
                    : isLocked
                    ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }
                `}
              >
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center text-2xl">
                  {isLocked ? <Lock className="h-6 w-6 text-muted-foreground" /> : tier.icon}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-sm">{tier.label}</h4>
                    {isSelected && !isLocked && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                    {tier.badge && (
                      <Badge variant={isLocked ? 'outline' : tier.badge.variant} className="text-[10px] px-1.5 py-0">
                        {tier.badge.text}
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mb-2">
                    {isLocked
                      ? getQualityUpgradeMessage(key, currentPlan)
                      : tier.description
                    }
                  </p>

                  {/* Specs */}
                  {!isLocked && (
                    <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                      <span className="bg-muted px-2 py-0.5 rounded">
                        {tier.resolution.label}
                      </span>
                      <span className="bg-muted px-2 py-0.5 rounded">
                        {formatResolution(tier.resolution.width, tier.resolution.height)}
                      </span>
                      {tier.estimatedFileSize && (
                        <span className="bg-muted px-2 py-0.5 rounded">
                          {tier.estimatedFileSize}
                        </span>
                      )}
                      {tier.processingTime && (
                        <span className="bg-muted px-2 py-0.5 rounded">
                          {tier.processingTime}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Upgrade CTA for locked tiers */}
                {isLocked && canUpgrade && (
                  <div className="flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpgrade();
                      }}
                      className="h-8 px-3 text-xs"
                    >
                      Upgrade
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Upgrade Banner for Free Users */}
        {currentPlan === 'free' && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-2xl">
                {QUALITY_TIERS['4k'].icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1">Upgrade for HD & 4K Exports</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Get professional 720p HD exports with Plus, or stunning 4K quality with Pro.
                </p>
                <Button
                  size="sm"
                  onClick={handleUpgrade}
                  className="h-8 text-xs"
                >
                  View Plans
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Plus User 4K Upgrade Banner */}
        {currentPlan === 'plus' && (
          <div className="bg-gradient-to-r from-primary/5 to-purple-500/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-2xl">
                {QUALITY_TIERS['4k'].icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1">Want 4K Ultra HD Quality?</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Upgrade to Pro for stunning 4K exports - perfect for client deliverables and premium content.
                </p>
                <Button
                  size="sm"
                  onClick={handleUpgrade}
                  className="h-8 text-xs"
                >
                  Upgrade to Pro
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatResolution(width: number, height: number): string {
  return `${width}×${height}`;
}
